import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Meta Tags Component
 * Dynamically updates page title, meta tags, and structured data
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = 'https://scopethemarket.com/og-image.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData = null,
}) => {
  const location = useLocation();
  const fullCanonical = canonical || `https://scopethemarket.com${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tag
    const setMetaTag = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const setLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Update meta description
    if (description) {
      setMetaTag('description', description);
    }

    // Update robots
    setMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Update canonical
    setLinkTag('canonical', fullCanonical);

    // Update Open Graph tags
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    setMetaTag('og:url', fullCanonical, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:site_name', 'ScopeTheMarket', true);
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
    }

    // Update Twitter tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', ogTitle || title);
    setMetaTag('twitter:description', ogDescription || description);
    if (ogImage) {
      setMetaTag('twitter:image', ogImage);
    }

    // Add/Update structured data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Cleanup function
    return () => {
      // Remove dynamic structured data on unmount
      const script = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, fullCanonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, noIndex, structuredData]);

  return null; // This component doesn't render anything
};

/**
 * Default Organization Schema for ScopeTheMarket
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "ScopeTheMarket",
  "url": "https://scopethemarket.com",
  "logo": "https://scopethemarket.com/logo.png",
  "description": "AI-powered stock market intelligence and anomaly detection platform",
  "sameAs": [
    "https://twitter.com/scopethemarket"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "url": "https://scopethemarket.com/about"
  }
});

/**
 * Website Schema
 */
export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "ScopeTheMarket",
  "url": "https://scopethemarket.com",
  "description": "AI-powered stock insights, anomaly detection, and real-time market intelligence",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://scopethemarket.com/stocks?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

/**
 * Stock Page Schema Generator
 */
export const getStockPageSchema = (symbol, companyName, price = null) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": `${companyName} (${symbol}) Stock Analysis`,
  "description": `Analyze ${companyName} (${symbol}) stock performance, trends, and AI-driven insights on ScopeTheMarket.`,
  "url": `https://scopethemarket.com/app/stock/${symbol}`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "ScopeTheMarket",
    "url": "https://scopethemarket.com"
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://scopethemarket.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Stocks",
        "item": "https://scopethemarket.com/stocks"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${symbol}`,
        "item": `https://scopethemarket.com/app/stock/${symbol}`
      }
    ]
  }
});

/**
 * Breadcrumb Schema Generator
 */
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

/**
 * Software Application Schema (for app pages)
 */
export const getSoftwareAppSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "ScopeTheMarket",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free tier available with Pro subscription for advanced features"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  }
});

export default SEOHead;
